import { useState } from "react";
import { Lock, Play } from "lucide-react";
import { AgentMini } from "@/components/viewer/TourExtras";
import { api, asset, errMsg } from "@/lib/api";
import { BrandMark } from "@/components/Brand";
import { assetLine } from "@/lib/industry";

export function PasswordGate({ hid, info, onUnlocked }) {
  const [pw, setPw] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try {
      const { data } = await api.post(`/public/house-types/${hid}/unlock`, { password: pw });
      sessionStorage.setItem(`tourkey_${hid}`, data.key);
      onUnlocked();
    } catch (x) { setErr(errMsg(x)); } finally { setBusy(false); }
  };
  return (
    <div className="h-screen bg-slate-950 text-white grid place-items-center p-6" data-testid="tour-password-gate">
      <form onSubmit={submit} className="w-full max-w-sm text-center">
        {info?.builder && <div className="flex justify-center mb-8"><BrandMark builder={info.builder} light /></div>}
        <span className="w-14 h-14 rounded-full bg-white/10 grid place-items-center mx-auto"><Lock className="w-6 h-6" /></span>
        <h1 className="font-serif text-3xl mt-5">{info?.name || "Private tour"}</h1>
        <p className="text-white/60 text-sm mt-2">This tour is private. Enter the password from the sales team.</p>
        <input type="password" autoFocus value={pw} onChange={(e) => setPw(e.target.value)} placeholder="Password" className="mt-6 w-full h-12 rounded-full bg-white/10 border border-white/15 px-5 text-center outline-none focus:border-white/40" data-testid="tour-password-input" />
        {err && <p className="text-rose-300 text-sm mt-3" data-testid="tour-password-error">{err}</p>}
        <button disabled={busy || !pw} className="mt-4 w-full h-12 rounded-full bg-white text-slate-900 font-semibold disabled:opacity-50" data-testid="tour-password-submit">{busy ? "Checking…" : "Open tour"}</button>
      </form>
    </div>
  );
}

export function StartScreen({ builder, house, onStart, agent }) {
  const bg = house.photos?.[0];
  return (
    <div className="absolute inset-0 z-50 grid place-items-center text-white" data-testid="tour-start-screen">
      {bg ? <img src={asset(bg)} alt="" className="absolute inset-0 w-full h-full object-cover" /> : <div className="absolute inset-0 bg-bp" />}
      <div className="absolute inset-0 bg-slate-950/65 backdrop-blur-[2px]" />
      <div className="relative text-center px-6 max-w-xl">
        <div className="flex justify-center"><BrandMark builder={builder} light size="lg" /></div>
        <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl mt-8">{house.name}</h1>
        {house.asset && assetLine(house.asset) && <p className="mt-3 text-sm sm:text-base text-white/80" data-testid="tour-start-asset">{assetLine(house.asset)}</p>}
        {house.asset && (house.asset.make || house.asset.serial || house.asset.size) && <p className="mt-1 text-xs text-white/60">{[house.asset.make, house.asset.size, house.asset.serial && `Serial ${house.asset.serial}`].filter(Boolean).join(" · ")}</p>}
        {(house.start_intro || house.description) && <p className="text-white/75 mt-4 text-sm sm:text-base line-clamp-4" data-testid="tour-start-intro">{house.start_intro || house.description}</p>}
        {agent && <div className="mt-8 flex justify-center"><div className="inline-flex rounded-full bg-white/10 border border-white/20 backdrop-blur-md pl-2 pr-5 py-2" data-testid="tour-start-agent"><AgentMini agent={agent} /></div></div>}
        <div className="mt-10 flex justify-center">
          <button onClick={onStart} className="h-16 pl-6 pr-8 rounded-full bg-white text-slate-900 font-semibold text-lg inline-flex items-center gap-3 shadow-2xl hover:scale-105 transition-transform" data-testid="tour-start-button">
            <span className="w-10 h-10 rounded-full bg-bp text-white grid place-items-center"><Play className="w-5 h-5 fill-current" /></span>Start tour
          </button>
        </div>
      </div>
    </div>
  );
}

export function Watermark({ builder }) {
  if (!builder.logo_url) return null;
  return <img src={asset(builder.logo_url)} alt="" className="absolute left-4 bottom-24 lg:bottom-6 z-[6] h-10 sm:h-12 w-auto opacity-35 pointer-events-none select-none" data-testid="tour-watermark" />;
}
